import React, { useState, useRef, useEffect, useMemo } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  Search,
  User,
  LogOut,
  ChevronDown,
  Menu,
  X,
} from 'lucide-react';
import searchCatalog from '../../search/catalog';
import { searchItems, visibleSearchItems } from '../../search/searchItems';

const typeLabels = {
  tool: 'Herramienta',
  doc: 'Documento',
  page: 'Página',
};

const Header = ({ user, roles, onToggleMobileMenu, isMobileMenuOpen, onNavigate }) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [resultTotal, setResultTotal] = useState(0);
  const profileRef = useRef(null);
  const searchableItems = useMemo(() => visibleSearchItems(searchCatalog, roles), [roles]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const openResult = (result) => {
    onNavigate(result.path);
    setSearchQuery('');
    setSearchResults([]);
    setResultTotal(0);
    setIsSearchFocused(false);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const { results, total } = searchItems(searchableItems, query);
    setSearchResults(results);
    setResultTotal(total);
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-border z-50">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left: Logo & Mobile Menu */}
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleMobileMenu}
            className="lg:hidden p-2 text-text-secondary hover:text-primary transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="flex items-center gap-3">
            <img
              src="/logo192.png"
              alt="Sonneil"
              className="h-8 w-8 rounded-lg"
            />
            <span className="hidden sm:block text-lg font-semibold text-text-primary">
              Sonneil Intranet
            </span>
          </div>
        </div>

        {/* Center: Search */}
        <div className="flex-1 max-w-xl mx-4 hidden md:block relative">
          <div
            className={`relative transition-all duration-200 ${
              isSearchFocused ? 'scale-[1.02]' : ''
            }`}
          >
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary"
            />
            <input
              type="text"
              placeholder="Buscar herramientas, documentos, páginas..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && searchResults[0]) {
                  event.preventDefault();
                  openResult(searchResults[0]);
                }
                if (event.key === 'Escape') {
                  setIsSearchFocused(false);
                }
              }}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              className="w-full pl-11 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm placeholder:text-text-tertiary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            />
          </div>

          {/* Search Results Dropdown */}
          {isSearchFocused && searchQuery.trim().length >= 3 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-card-hover border border-border overflow-hidden animate-fade-in z-50">
              {searchResults.length > 0 ? (
                <>
                  {searchResults.map((result) => (
                    <button
                      key={`${result.type}-${result.path}`}
                      onClick={() => openResult(result)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background transition-colors text-left"
                    >
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                          result.type === 'tool'
                            ? 'bg-primary-lighter text-primary-dark'
                            : result.type === 'doc'
                            ? 'bg-info/10 text-info'
                            : 'bg-warning/10 text-warning'
                        }`}
                      >
                        {typeLabels[result.type] || 'Página'}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-text-primary truncate">{result.title}</span>
                        {result.section && (
                          <span className="block text-xs text-text-tertiary truncate">{result.section}</span>
                        )}
                      </span>
                    </button>
                  ))}
                  {resultTotal > searchResults.length && (
                    <p className="px-4 py-2 text-xs text-text-tertiary border-t border-border-light">
                      {searchResults.length} de {resultTotal} resultados. Afina la búsqueda para ver el resto.
                    </p>
                  )}
                </>
              ) : (
                <p className="px-4 py-3 text-sm text-text-secondary">
                  No hay secciones que coincidan con “{searchQuery.trim()}”.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Profile */}
          <div ref={profileRef} className="relative">
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2 p-2 hover:bg-primary-lighter/30 rounded-lg transition-colors"
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-lighter flex items-center justify-center">
                  <User size={16} className="text-primary" />
                </div>
              )}
              <span className="hidden lg:block text-sm font-medium text-text-primary max-w-32 truncate">
                {user?.displayName || 'Usuario'}
              </span>
              <ChevronDown size={16} className="hidden lg:block text-text-tertiary" />
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-card-hover border border-border overflow-hidden animate-fade-in">
                <div className="px-4 py-3 border-b border-border-light">
                  <p className="font-medium text-text-primary truncate">
                    {user?.displayName || 'Usuario'}
                  </p>
                  <p className="text-xs text-text-tertiary truncate">
                    {user?.email || ''}
                  </p>
                </div>
                <div className="py-1">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2 text-error hover:bg-error/5 transition-colors"
                  >
                    <LogOut size={16} />
                    <span className="text-sm">Cerrar sesión</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;


